import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, ShoppingCart, User, Building2, Package, Calendar, FileText, Loader2, Wifi, WifiOff, Globe } from 'lucide-react';
import { useOrderWebSocket } from '@/hooks/useOrderWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResellerOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');

  // Storefront Requests tab state
  const [wlRequests, setWlRequests] = useState([]);
  const [wlReqLoading, setWlReqLoading] = useState(false);
  const [wlUpdating, setWlUpdating] = useState(null); // request_id being updated
  const [downloadingInvoice, setDownloadingInvoice] = useState(null); // order_id being invoiced

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownloadOrderInvoice = async (orderId) => {
    setDownloadingInvoice(orderId);
    try {
      const res = await axios.get(`${API}/partner/orders/${orderId}/invoice`);
      const { pdf_data, filename } = res.data;
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_data}`;
      link.download = filename || `Invoice_${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const fetchWlRequests = useCallback(async () => {
    setWlReqLoading(true);
    try {
      const r = await axios.get(`${API}/white-label/requests`);
      setWlRequests(r.data);
    } catch { toast.error('Failed to load storefront requests'); }
    finally { setWlReqLoading(false); }
  }, []);

  const handleWlStatusChange = async (reqId, newStatus) => {
    setWlUpdating(reqId);
    try {
      await axios.put(`${API}/white-label/requests/${reqId}/status`, { status: newStatus });
      setWlRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: newStatus } : r));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setWlUpdating(null); }
  };
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[Orders WS] Received:', message);
    
    if (message.type === 'order_provisioned') {
      toast.success(`🎉 Service Activated: ${message.data?.product_name || 'Your service'}`, {
        description: 'Login credentials are now available in your dashboard'
      });
      fetchOrders();
    } else if (message.type === 'order_status_changed') {
      const statusText = message.data?.new_status?.replace('_', ' ');
      toast.info(`Order status changed to: ${statusText}`);
      fetchOrders();
    } else if (message.type === 'order_cancelled') {
      toast.warning(`Order cancelled: ${message.data?.product_name || 'Service'}`);
      fetchOrders();
    } else if (message.type === 'order_activated') {
      toast.success(`Order activated: ${message.data?.product_name || 'Service'}`);
      fetchOrders();
    }
  }, [fetchOrders]);

  const { isConnected: wsConnected } = useOrderWebSocket(handleWebSocketMessage);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Lazy-load storefront requests when that tab is first opened
  useEffect(() => {
    if (activeTab === 'storefront') fetchWlRequests();
  }, [activeTab, fetchWlRequests]);

  const fetchOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setSelectedOrder(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Failed to fetch order details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700';
      case 'pending': return 'bg-yellow-50 text-yellow-700';
      case 'suspended': return 'bg-red-50 text-red-700';
      case 'cancelled': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const viewOrderDetails = (order) => {
    fetchOrderDetails(order.id);
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.source_partner?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesTab = activeTab === 'all' || order.status === activeTab;
    const matchesSource = sourceFilter === 'all' || 
                          (sourceFilter === 'self' && !order.is_channel_partner_order) ||
                          (sourceFilter === 'channel_partner' && order.is_channel_partner_order);
    return matchesSearch && matchesStatus && matchesTab && matchesSource;
  });

  const tabs = [
    { id: 'all', label: 'All Orders' },
    { id: 'active', label: 'Active' },
    { id: 'pending', label: 'Pending' },
    { id: 'suspended', label: 'Suspended' },
    { id: 'storefront', label: 'Storefront Requests' },
  ];

  // Check if user is a Synergy Partner (reseller)
  const isSynergyPartner = user?.role === 'reseller';
  
  // Count orders by source for stats
  const selfOrdersCount = orders.filter(o => !o.is_channel_partner_order).length;
  const channelPartnerOrdersCount = orders.filter(o => o.is_channel_partner_order).length;

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-800" data-testid="orders-title">Orders</h1>
            {wsConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" title="Real-time updates active">
                <Wifi className="h-3 w-3" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full" title="Connecting...">
                <WifiOff className="h-3 w-3" />
                Offline
              </span>
            )}
          </div>
          <p className="text-slate-500 mt-1">
            {isSynergyPartner 
              ? `View and manage your orders and Channel Partner orders (${selfOrdersCount} own + ${channelPartnerOrdersCount} from partners)`
              : 'View and manage your orders'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-2 sm:gap-5 md:gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-colors relative text-nowrap
                  ${activeTab === tab.id 
                    ? 'text-blue-600' 
                    : 'text-slate-500 hover:text-slate-700'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {/* Filters (only for orders tabs) */}
        {activeTab !== 'requests' && (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-28 md:w-32 bg-white" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                
                {isSynergyPartner && (
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-28 md:w-44 bg-white" data-testid="source-filter">
                      <SelectValue placeholder="Order Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Orders</SelectItem>
                      <SelectItem value="self">My Orders</SelectItem>
                      <SelectItem value="channel_partner">Channel Partner Orders</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by product, ID, or partner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-24 md:w-80 bg-white !text-sm md:!text-base"
                  data-testid="search-orders"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Orders Table */}
        <Card className="bg-white border-0 shadow-sm" data-testid="orders-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Order ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Product</th>
                      {isSynergyPartner && (
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Source</th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Billing Cycle</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 text-nowrap">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-sm text-slate-600 text-nowrap">
                          {order.id.substring(0, 8)}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800 text-nowrap">
                          {order.product_name}
                          {order.source === 'client_request' && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700" data-testid={`client-req-badge-${order.id}`}>
                              Client Request
                            </span>
                          )}
                        </td>
                        {isSynergyPartner && (
                          <td className="px-4 py-3 text-nowrap">
                            <div className="flex items-center gap-2">
                              {order.is_channel_partner_order ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                  <Building2 className="h-3 w-3" />
                                  {order.source_partner}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                  <User className="h-3 w-3" />
                                  Self
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-slate-600 capitalize text-nowrap">
                          {order.billing_cycle?.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-800 text-right text-nowrap">
                          ₹{(order.sp_price_total ?? order.amount)?.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-nowrap">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium capitalize ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewOrderDetails(order)}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              data-testid={`view-order-${order.id}`}
                              disabled={loadingDetails}
                            >
                              {loadingDetails ? 'Loading...' : 'View Details'}
                            </button>
                            {!['pending', 'processing', 'pending_payment'].includes(order.status?.toLowerCase()) && (
                              <>
                                <span className="text-slate-300">|</span>
                                <button
                                  onClick={() => handleDownloadOrderInvoice(order.id)}
                                  className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline font-medium flex items-center gap-1"
                                  data-testid={`download-order-invoice-${order.id}`}
                                  disabled={downloadingInvoice === order.id}
                                >
                                  {downloadingInvoice === order.id
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <FileText className="h-3 w-3" />}
                                  Invoice
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Order Details
                {selectedOrder?.is_channel_partner_order && (
                  <Badge variant="outline" className="ml-2 bg-purple-50 text-purple-700 border-purple-200">
                    Channel Partner Order
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : selectedOrder && (
              <div className="space-y-6 py-2">
                {/* Order Info */}
                <div className="bg-[#fff6f6] rounded-lg p-4 mt-2">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Order Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Order ID</p>
                      <p className="font-mono text-sm">{selectedOrder.id}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Status</p>
                      <Badge className={getStatusBadgeClass(selectedOrder.status)}>
                        {selectedOrder.status}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Amount</p>
                      <p className="text-lg font-bold text-blue-600">₹{(selectedOrder.sp_price_total ?? selectedOrder.amount)?.toLocaleString()}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Billing Cycle</p>
                      <p className="text-sm capitalize">{selectedOrder.billing_cycle?.replace('_', ' ')}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Quantity</p>
                      <p className="text-sm">{selectedOrder.quantity || 1}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500">Order Date</p>
                      <p className="text-sm">{formatDate(selectedOrder.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Client Information */}
                {selectedOrder.client && (
                  <div className="bg-blue-50 rounded-lg p-4 mt-2">
                    <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Client Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-blue-600">Company Name</p>
                        <p className="text-sm font-medium">{selectedOrder.client.company_name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-blue-600">Email</p>
                        <p className="text-sm">{selectedOrder.client.email}</p>
                      </div>
                      {selectedOrder.client.phone && (
                        <div className="space-y-1">
                          <p className="text-xs text-blue-600">Phone</p>
                          <p className="text-sm">{selectedOrder.client.phone}</p>
                        </div>
                      )}
                      {selectedOrder.client.gst_number && (
                        <div className="space-y-1">
                          <p className="text-xs text-blue-600">GST Number</p>
                          <p className="text-sm font-mono">{selectedOrder.client.gst_number}</p>
                        </div>
                      )}
                      {selectedOrder.client.address && (
                        <div className="space-y-1 col-span-2">
                          <p className="text-xs text-blue-600">Address</p>
                          <p className="text-sm">{selectedOrder.client.address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Product Information */}
                {selectedOrder.product && (
                  <div className="bg-green-50 rounded-lg p-4 mt-2">
                    <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Product Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-green-600">Product Name</p>
                        <p className="text-sm font-medium">{selectedOrder.product.name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-green-600">Category</p>
                        <p className="text-sm">{selectedOrder.product.category}</p>
                      </div>
                      <div className="space-y-1 col-span-2">
                        <p className="text-xs text-green-600">Description</p>
                        <p className="text-sm text-slate-600">{selectedOrder.product.description}</p>
                      </div>
                      {selectedOrder.product.features && selectedOrder.product.features.length > 0 && (
                        <div className="space-y-2 col-span-2">
                          <p className="text-xs text-green-600">Features</p>
                          <ul className="text-sm space-y-1">
                            {selectedOrder.product.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Channel Partner Information (for Synergy Partner viewing CP's order) */}
                {selectedOrder.is_channel_partner_order && selectedOrder.order_reseller && (
                  <div className="bg-purple-50 rounded-lg p-4 mt-2">
                    <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Channel Partner (Order Source)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-purple-600">Company Name</p>
                        <p className="text-sm font-medium">{selectedOrder.order_reseller.company_name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-purple-600">Email</p>
                        <p className="text-sm">{selectedOrder.order_reseller.email}</p>
                      </div>
                      {selectedOrder.order_reseller.phone && (
                        <div className="space-y-1">
                          <p className="text-xs text-purple-600">Phone</p>
                          <p className="text-sm">{selectedOrder.order_reseller.phone}</p>
                        </div>
                      )}
                      {selectedOrder.order_reseller.gst_number && (
                        <div className="space-y-1">
                          <p className="text-xs text-purple-600">GST Number</p>
                          <p className="text-sm font-mono">{selectedOrder.order_reseller.gst_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quotation Info */}
                {selectedOrder.quotation && (
                  <div className="bg-amber-50 rounded-lg p-4 mt-2">
                    <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Quotation Reference
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-amber-600">Quotation ID</p>
                        <p className="font-mono text-sm">{selectedOrder.quotation.id}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-amber-600">Validity</p>
                        <p className="text-sm">{selectedOrder.quotation.validity_days} days</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedOrder.notes && (
                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500 mb-1 font-medium">Order Notes</p>
                    <p className="text-sm bg-[#115ab4]/10 p-3 rounded">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Storefront Requests Tab ── */}
        {activeTab === 'storefront' && (
          <div className="space-y-4 !mt-4" data-testid="storefront-requests-section">
            <div className="flex items-center justify-between">
              <div className="mb-2">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Storefront Requests
                </h2>
                <p className="text-sm text-slate-500">Order inquiries submitted via your white-label storefront</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchWlRequests} disabled={wlReqLoading} data-testid="refresh-wl-requests">
                Refresh
              </Button>
            </div>

            {wlReqLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : wlRequests.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Globe className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No storefront requests yet</p>
                <p className="text-sm text-slate-400 mt-1">Requests submitted from your white-label storefront will appear here</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Mobile</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Plan/Service</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {wlRequests.map(req => (
                      <tr key={req.id} className="hover:bg-slate-50" data-testid={`wl-req-row-${req.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{req.visitor_name}</td>
                        <td className="px-4 py-3 text-slate-600">{req.visitor_mobile}</td>
                        <td className="px-4 py-3 text-slate-600">{req.visitor_email}</td>
                        <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{req.product_name}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {new Date(req.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={req.status}
                            onValueChange={val => handleWlStatusChange(req.id, val)}
                            disabled={wlUpdating === req.id}
                          >
                            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`wl-status-${req.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="New">New</SelectItem>
                              <SelectItem value="Contacted">Contacted</SelectItem>
                              <SelectItem value="Converted">Converted</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
